import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Globe } from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = "https://www.mypanther.fr/web/image/website/1/logo/My%20Panther?unique=501ee63";

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const { login } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      // Redirect based on role/partner_type
      const userData = result.user || {};
      if (userData.partner_type === 'designer') {
        navigate('/designer-dashboard');
      } else if (userData.partner_type === 'manager') {
        navigate('/manager-dashboard');
      } else if (userData.role === 'partenaire') {
        navigate('/partner-dashboard');
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(result.error);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
      toast.success(t('reset_email_sent'));
    } catch (err) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="absolute top-4 right-4">
        <Select value={language} onValueChange={setLanguage}>
          <SelectTrigger data-testid="login-lang-switcher" className="w-28 h-8 text-xs border-slate-200 bg-white">
            <Globe size={14} className="mr-1" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="fr">Français</SelectItem>
            <SelectItem value="en">English</SelectItem>
            <SelectItem value="vi">Tiếng Việt</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Panther" data-testid="login-logo" className="h-16 mx-auto mb-4 object-contain" />
          <p className="text-sm text-slate-600">{t('order_management')}</p>
        </div>
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl">{t('login')}</CardTitle>
            <CardDescription>{t('login_subtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div data-testid="login-error" className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">{t('email')}</Label>
                <Input
                  data-testid="login-email-input"
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="border-slate-200"
                  placeholder="votre@email.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t('password')}</Label>
                <Input
                  data-testid="login-password-input"
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="border-slate-200"
                  placeholder="••••••••"
                />
              </div>
              <Button
                data-testid="login-submit-button"
                type="submit"
                className="w-full bg-slate-900 hover:bg-slate-800"
                disabled={loading}
              >
                {loading ? t('connecting') : t('connect')}
              </Button>
              <button
                type="button"
                data-testid="forgot-password-link"
                onClick={() => setShowForgot(true)}
                className="w-full text-center text-sm text-slate-500 hover:text-slate-800 underline mt-2"
              >
                {t('forgot_password')}
              </button>
            </form>
          </CardContent>
        </Card>

        {showForgot && (
          <Card className="border-slate-200 shadow-sm mt-4">
            <CardHeader>
              <CardTitle className="text-lg">{t('forgot_password')}</CardTitle>
              <CardDescription>{t('forgot_password_desc')}</CardDescription>
            </CardHeader>
            <CardContent>
              {forgotSent ? (
                <div data-testid="forgot-success" className="text-center py-2">
                  <p className="text-emerald-700 text-sm font-medium">{t('reset_email_sent')}</p>
                  <button onClick={() => { setShowForgot(false); setForgotSent(false); }} className="text-sm text-slate-500 underline mt-2">{t('back_to_login')}</button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-3">
                  <div className="space-y-2">
                    <Label>{t('email')}</Label>
                    <Input
                      data-testid="forgot-email-input"
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      placeholder="votre@email.com"
                      className="border-slate-200"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      data-testid="forgot-submit-button"
                      type="submit"
                      className="flex-1 bg-slate-900 hover:bg-slate-800"
                      disabled={forgotLoading}
                    >
                      {forgotLoading ? '...' : t('send_reset_link')}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowForgot(false)}>{t('cancel')}</Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
